import { useState, useCallback, useEffect, useRef } from 'react';
import { isFileType } from '../../../utils/formCrafter/fieldTypeUtils';

/**
 * useFormDataManager - Hook for managing form data state across subforms
 *
 * @param {Array} subforms - Array of subform objects
 * @param {Object} parsedFormData - Parsed form data from useFormCrafterSchema
 * @param {Object} schemas - Schema objects by subform ID
 * @param {Object} filledForm - The filled form object (for tracking changes)
 * @returns {Object} - Form data state and methods
 */
const useFormDataManager = (subforms, parsedFormData, schemas, filledForm) => {
  const [formDataBySubform, setFormDataBySubform] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Track the filledForm.id we've already processed to prevent re-initialization
  const processedFilledFormIdRef = useRef(null);

  // Initialize form data with filled values
  useEffect(() => {
    if (processedFilledFormIdRef.current === filledForm?.id) {
      return;
    }

    if (subforms.length > 0 && filledForm) {
      try {
        const initialData = {};

        subforms.forEach((subform) => {
          const subformIdStr = String(subform.id);
          const data =
            parsedFormData[subformIdStr] || parsedFormData[subform.id];

          if (data) {
            const schema = schemas[subform.id];
            const processedData = { ...data };

            // Clear file field values (handled separately via useFileAttachments)
            if (schema?.fields) {
              schema.fields.forEach((field) => {
                if (isFileType(field.type)) {
                  processedData[field.id] = [];
                }
              });
            }

            initialData[subform.id] = processedData;
          }
        });

        setFormDataBySubform(initialData);
        setIsDataLoaded(true);
        processedFilledFormIdRef.current = filledForm?.id;
      } catch (error) {
        console.error('Error initializing form data:', error);
      }
    } else if (!filledForm && subforms.length > 0 && schemas) {
      // NEW REQUEST: Initialize with default values from schema
      const initialData = {};

      subforms.forEach((subform) => {
        const schema = schemas[subform.id];
        if (schema?.fields) {
          const subformDefaults = {};
          schema.fields.forEach((field) => {
            // Check for defaultValue property (form builder format)
            if (
              field.defaultValue !== undefined &&
              field.defaultValue !== null &&
              field.defaultValue !== ''
            ) {
              subformDefaults[field.id] = field.defaultValue;
            }
            // Also check for value property (legacy format)
            else if (
              field.value !== undefined &&
              field.value !== null &&
              field.value !== ''
            ) {
              subformDefaults[field.id] = field.value;
            }
          });
          if (Object.keys(subformDefaults).length > 0) {
            initialData[subform.id] = subformDefaults;
          }
        }
      });

      setFormDataBySubform(initialData);
      setIsDataLoaded(true);
    } else if (!filledForm) {
      setIsDataLoaded(true);
    }
  }, [subforms, filledForm, schemas, parsedFormData]);

  // Update a single field value
  const updateFieldValue = useCallback((subformId, fieldId, value) => {
    setFormDataBySubform((prev) => {
      const newState = {
        ...prev,
        [subformId]: {
          ...(prev[subformId] || {}),
          [fieldId]: value,
        },
      };
      return newState;
    });
  }, []);

  // Get form data for a specific subform
  const getSubformData = useCallback(
    (subformId) => {
      return formDataBySubform[subformId] || {};
    },
    [formDataBySubform]
  );

  // Collect all form data
  const collectAllFormData = useCallback(
    (subformsList) => {
      const collectedData = {};
      subformsList.forEach((subform) => {
        collectedData[subform.id] = formDataBySubform[subform.id] || {};
      });
      return collectedData;
    },
    [formDataBySubform]
  );

  return {
    formDataBySubform,
    isDataLoaded,
    updateFieldValue,
    getSubformData,
    collectAllFormData,
  };
};

export default useFormDataManager;
